import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Tag, Select, Typography, Divider, Tabs } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { tenantService, CreateUserRequest, UserInfo, Tenant } from '../services/tenantService';
import UserSettings from './UserSettings';
import './common.css';

const { Title } = Typography;

const TenantManager: React.FC = () => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [userForm] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [filterUsername, setFilterUsername] = useState('');
  const [filterTenant, setFilterTenant] = useState<string | undefined>(undefined);
  const [editForm] = Form.useForm();
  const [editPwdForm] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    loadUsers();
    loadTenants();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getAllUsers();
      setUsers(data);
    } catch (error: any) {
      message.error('Failed to load user list');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const data = await tenantService.getAllTenants();
      setTenants(data);
    } catch (error: any) {
      message.error('Failed to load tenant list');
      console.error(error);
    }
  };

  const handleCreateUser = async (values: any) => {
    try {
      // 转换租户数据格式
      const requestData: CreateUserRequest = {
        username: values.username,
        password: values.password,
        isSuperAdmin: values.isSuperAdmin || false,
        tenant_code: values.tenant_code || 'default'
      };

      const response = await tenantService.createUser(requestData);
      
      // Handle warning messages in response
      if ((response as any).warnings && (response as any).warnings.length > 0) {
        message.warning(response.message);
        (response as any).warnings.forEach((warning: string) => {
          message.warning(warning);
        });
      } else {
        message.success('User created successfully');
      }
      
      setUserModalVisible(false);
      userForm.resetFields();
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleResetPassword = (userId: number) => {
    // 测试message组件是否工作
    message.info('测试消息提示功能');
    
    setResetPasswordUserId(userId);
    setResetPasswordModalVisible(true);
    resetPasswordForm.resetFields();
  };

  const handleResetPasswordSubmit = async (values: any) => {
    if (!resetPasswordUserId) return;
    
    setResetPasswordLoading(true);
    
    try {
      console.log('Starting password reset for user:', resetPasswordUserId);
      console.log('New password:', values.newPassword ? '***' : 'null');
      
      const result = await tenantService.resetUserPassword(resetPasswordUserId, values.newPassword);
      console.log('Password reset result:', result);
      
      // 成功提示
      console.log('Showing success message');
      message.success('密码重置成功！');
      
      setResetPasswordModalVisible(false);
      resetPasswordForm.resetFields();
      setResetPasswordUserId(null);
    } catch (error: any) {
      console.error('Reset password error:', error);
      
      // 失败提示
      console.log('Showing error message:', error.response?.data?.error || '密码重置失败，请重试');
      message.error(error.response?.data?.error || '密码重置失败，请重试');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModalVisible(false);
    resetPasswordForm.resetFields();
    setResetPasswordUserId(null);
  };

  const openEditModal = (user: UserInfo) => {
    setEditingUser(user);
    setEditModalVisible(true);
    editForm.setFieldsValue({
      username: user.username,
      tenant_code: user.tenant_code,
      isSuperAdmin: user.is_super_admin,
    });
    editPwdForm.resetFields();
  };

  const handleEditSubmit = async (values: any) => {
    if (!editingUser) return;
    setSavingEdit(true);
    try {
      await tenantService.updateUser(editingUser.id, {
        username: values.username,
        tenant_code: values.tenant_code,
        isSuperAdmin: values.isSuperAdmin || false,
      });
      message.success('User updated successfully');
      setEditModalVisible(false);
      setEditingUser(null);
      editForm.resetFields();
      await loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditPasswordSubmit = async (vals: any) => {
    if (!editingUser) return;
    setSavingPwd(true);
    try {
      await tenantService.resetUserPassword(editingUser.id, vals.newPassword);
      message.success('密码已重置');
      editPwdForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || '密码重置失败');
    } finally {
      setSavingPwd(false);
    }
  };

  const userColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_code',
      key: 'tenant_code',
      width: 120,
      render: (tenantCode: string) => (
        <Tag color="blue">{tenantCode}</Tag>
      ),
    },
    {
      title: 'Super Admin',
      dataIndex: 'is_super_admin',
      key: 'is_super_admin',
      width: 100,
      render: (isSuperAdmin: boolean) => (
        <Tag color={isSuperAdmin ? 'red' : 'blue'}>
          {isSuperAdmin ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (record: UserInfo) => (
        <Space>
          <Button
            size="small"
            type="link"
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          <Button
            size="small"
            onClick={() => handleResetPassword(record.id)}
          >
            Reset Password
          </Button>
        </Space>
      ),
    },
  ];

  const filteredUsers = users.filter(u => {
    const matchName = filterUsername.trim() ? u.username.toLowerCase().includes(filterUsername.trim().toLowerCase()) : true;
    const matchTenant = filterTenant ? u.tenant_code === filterTenant : true;
    return matchName && matchTenant;
  });

  const resetFilters = () => {
    setFilterUsername('');
    setFilterTenant(undefined);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <Title level={2}>User Management</Title>
        <p>Manage system users and their permissions</p>
      </div>

      <div className="page-content">
        <div className="table-header">
          <Space wrap>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setUserModalVisible(true)}
            >
              Create User
            </Button>
            <Divider type="vertical" />
            <Input
              style={{ width: 220 }}
              placeholder="Search username"
              allowClear
              value={filterUsername}
              onChange={(e) => setFilterUsername(e.target.value)}
            />
            <Select
              allowClear
              placeholder="Filter by tenant"
              style={{ width: 220 }}
              value={filterTenant}
              onChange={(val) => setFilterTenant(val)}
            >
              {tenants.map(t => (
                <Select.Option key={t.code} value={t.code}>
                  {t.name} ({t.code})
                </Select.Option>
              ))}
            </Select>
            <Button onClick={resetFilters}>Reset</Button>
          </Space>
        </div>

        <Table
          columns={userColumns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`
          }}
        />
      </div>

      {/* User Creation Modal */}
      <Modal
        title="Create User"
        open={userModalVisible}
        onCancel={() => {
          setUserModalVisible(false);
          userForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please enter username' }]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter password' }]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            name="tenant_code"
            label="Tenant"
            initialValue="default"
          >
            <Select placeholder="Select tenant">
              {tenants.map(tenant => (
                <Select.Option key={tenant.code} value={tenant.code}>
                  {tenant.name} ({tenant.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isSuperAdmin"
            label="Super Admin"
            valuePropName="checked"
          >
            <input type="checkbox" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create User
              </Button>
              <Button onClick={() => {
                setUserModalVisible(false);
                userForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑用户弹框 */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingUser(null);
          editForm.resetFields();
          editPwdForm.resetFields();
        }}
        footer={null}
        width={720}
      >
        <Tabs
          defaultActiveKey="basic"
          items={[
            {
              key: 'basic',
              label: 'Basic',
              children: (
                <Form
                  form={editForm}
                  layout="vertical"
                  onFinish={handleEditSubmit}
                >
                  <Form.Item
                    name="username"
                    label="Username"
                    rules={[{ required: true, message: 'Please enter username' }]}
                  >
                    <Input placeholder="Enter username" />
                  </Form.Item>

                  <Form.Item
                    name="tenant_code"
                    label="Tenant"
                    rules={[{ required: true, message: 'Please select tenant' }]}
                  >
                    <Select placeholder="Select tenant">
                      {tenants.map(tenant => (
                        <Select.Option key={tenant.code} value={tenant.code}>
                          {tenant.name} ({tenant.code})
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="isSuperAdmin"
                    label="Super Admin"
                    valuePropName="checked"
                  >
                    <input type="checkbox" />
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button type="primary" htmlType="submit" loading={savingEdit} disabled={savingEdit}>
                        Save
                      </Button>
                      <Button
                        onClick={() => {
                          setEditModalVisible(false);
                          setEditingUser(null);
                        }}
                        disabled={savingEdit}
                      >
                        Cancel
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'password',
              label: 'Password',
              children: (
                <Form
                  form={editPwdForm}
                  layout="vertical"
                  onFinish={handleEditPasswordSubmit}
                >
                  <Form.Item
                    name="newPassword"
                    label="新密码"
                    rules={[
                      { required: true, message: '请输入新密码' },
                      { min: 6, message: '密码至少需要6位字符' },
                      { max: 50, message: '密码不能超过50位字符' },
                      {
                        pattern: /^(?=.*[a-zA-Z])(?=.*\d)/,
                        message: '密码必须包含至少一个字母和一个数字'
                      }
                    ]}
                  >
                    <Input.Password placeholder="请输入新密码（至少6位，包含字母和数字）" autoComplete="new-password" />
                  </Form.Item>

                  <Form.Item
                    name="confirmPassword"
                    label="确认密码"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: '请确认新密码' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('两次输入的密码不一致'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="请再次输入新密码" autoComplete="new-password" />
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button type="primary" htmlType="submit" loading={savingPwd} disabled={savingPwd}>
                        重置密码
                      </Button>
                      <Button onClick={() => editPwdForm.resetFields()} disabled={savingPwd}>
                        清空
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'preferences',
              label: 'Preferences',
              children: (
                <div>
                  <p style={{ color: '#999', marginBottom: 12 }}>当前偏好设置为本机浏览器级别示例，暂不支持为其他用户保存。</p>
                  <UserSettings embedded />
                </div>
              )
            }
          ]}
        />
      </Modal>
      {/* 密码重置弹框 */}
      <Modal
        title="重置密码"
        open={resetPasswordModalVisible}
        onCancel={closeResetPasswordModal}
        footer={null}
        width={500}
      >
        <Form
          form={resetPasswordForm}
          layout="vertical"
          onFinish={handleResetPasswordSubmit}
        >
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少需要6位字符' },
              { max: 50, message: '密码不能超过50位字符' },
              {
                pattern: /^(?=.*[a-zA-Z])(?=.*\d)/,
                message: '密码必须包含至少一个字母和一个数字'
              }
            ]}
          >
            <Input.Password 
              placeholder="请输入新密码（至少6位，包含字母和数字）"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password 
              placeholder="请再次输入新密码"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={resetPasswordLoading}
                disabled={resetPasswordLoading}
              >
                {resetPasswordLoading ? '重置中...' : '重置密码'}
              </Button>
              <Button 
                onClick={closeResetPasswordModal}
                disabled={resetPasswordLoading}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TenantManager;